import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from "ngx-logger";
import { map, take } from 'rxjs/operators';
import { CountryMetadata, GeolocationResult } from "../models/data-model";
import { DTRUM_ACTION_NAME, DynatraceHelperService } from "./dynatraceHelper.service";
import { RegistrationApiService } from "./registration-api.service";
import { SoldoCookieService } from "./soldo-cookie.service";

export const MAIN_MARKETS_COUNTRY_ISO: string[] = ["FRA", "DEU", "ITA", "NLD", "ESP", "GBR"];

@Injectable()
export class CountryService  {
    private countriesMeta: CountryMetadata[] = [];
    private readonly translatedCountriesCache: Map<string, CountryMetadata[]> = new Map();

    constructor(
        private readonly http: HttpClient,
        private readonly registrationApiService: RegistrationApiService,
        private readonly translate: TranslateService,
        private readonly soldoCookieService: SoldoCookieService,
        private readonly logger: NGXLogger,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    getCurrentCountries(): Promise<CountryMetadata[]> {
        return this.fetchTranslatedCountries(this.translate.getCurrentLang());
    }

    async preloadMetadata(): Promise<CountryMetadata[]> {
        if (this.countriesMeta.length === 0) {
            this.countriesMeta = await this.loadMetadata();
        }
        return this.countriesMeta;
    }

    getCachedMetadataByIso3(iso3: string): CountryMetadata | undefined {
        if (this.countriesMeta.length === 0) {
            throw new Error('CountryService.findByIso3 called before metadata was preloaded');
        }
        return this.countriesMeta.find(c => c.iso3 === iso3);
    }

    async fetchTranslatedCountries(lang: string): Promise<CountryMetadata[]> {
        await this.preloadMetadata();
        const cached = this.translatedCountriesCache.get(lang);
        if (cached) {
            return cached;
        }
        const translateCountriesActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.TRANSLATE_COUNTRIES);
        try {
            const translated = await this.translateCountriesNames(lang);
            this.translatedCountriesCache.set(lang, translated);
            return translated;
        } finally {
            this.dynatrace.leaveAction(translateCountriesActionId);
        }
    }

    async getGeolocation(): Promise<GeolocationResult> {
        let conf = this.soldoCookieService.getStoredConfig();
        if (conf.geolocation) {
            this.logger.debug("Country Service, location retrieved from conf cookie ", conf.geolocation);
            return { countryIso3: conf.geolocation, outcome: 'cached' };
        }

        let iso2: string;
        try {
            iso2 = (await this.geolocate())?.country_code;
        } catch (e) {
            this.logger.debug("Country Service, geolocation lookup failed", e);
            return { countryIso3: null, outcome: 'failed' };
        }
        if (!iso2) {
            this.logger.debug("Country Service, geolocation returned no country_code");
            return { countryIso3: null, outcome: 'failed' };
        }

        const countriesMetadata = await this.getCurrentCountries();
        const match = countriesMetadata.find(c => c.iso2 === iso2);
        if (!match) {
            this.logger.debug("Country Service, geolocation returned an unmapped country ", iso2);
            return { countryIso3: null, outcome: 'unmapped' };
        }

        conf = this.soldoCookieService.getStoredConfig();
        conf.geolocation = match.iso3;
        this.soldoCookieService.storeConfig(conf);
        return { countryIso3: match.iso3, outcome: 'resolved' };
    }

    private geolocate(): Promise<{ country_code: string }> {
        const geolocationService = new URL("https://utils.soldo.com/checkip");
        const observable = this.http.get<{ country_code: string }>(geolocationService.toString());
        return observable.pipe(take(1)).toPromise();
    }

    sortFunction = function (a: CountryMetadata, b: CountryMetadata): number {
        if (a.name > b.name) {
            return 1;
        } else if (a.name < b.name) {
            return -1;
        } else {
            return 0;
        }
    };

    getMainMarkets(): Array<string> {
        return MAIN_MARKETS_COUNTRY_ISO;
    }

    private loadMetadata(): Promise<CountryMetadata[]> {
        return this.registrationApiService.getWorldCountriesMetadata().toPromise();
    }

    private translateCountriesNames(lang: string): Promise<CountryMetadata[]> {
        return this.registrationApiService.getWorldCountriesNames(lang)
            .pipe(
                map(names => this.countriesMeta.map(meta => ({
                    ...meta,
                    name: names.find(n => n.iso3 === meta.iso3).name
                })))
            ).toPromise();
    }

}
